/**
 * Downline change requests — an upline asking us to change contract type,
 * commission type, hierarchy, or level on one of their downline's carrier
 * contracts.
 *
 * The request is a proposal, never an applied change: staff review it and
 * carry it out with the carrier. Everything here is pure so both the portal
 * form and the API validate the payload the same way.
 */
import { CARRIER_SLUG_TO_CARRIER, CARRIER_DISPLAY_NAMES } from "./carriers.js";
export const DOWNLINE_CHANGE_CONTRACT_TYPES = ["INDIVIDUAL", "AGENCY", "NO_CHANGE"];
export const DOWNLINE_CHANGE_COMMISSION_TYPES = ["ADVANCED", "AS_EARNED", "NO_CHANGE"];
export const DOWNLINE_CHANGE_CONTRACT_TYPE_LABELS = {
    INDIVIDUAL: "Individual",
    AGENCY: "Agency",
    NO_CHANGE: "No change",
};
export const DOWNLINE_CHANGE_COMMISSION_TYPE_LABELS = {
    ADVANCED: "Advanced",
    AS_EARNED: "As earned",
    NO_CHANGE: "No change",
};
export const DOWNLINE_CHANGE_REQUEST_STATUSES = [
    "SUBMITTED",
    "APPROVED",
    "REJECTED",
    "CANCELLED",
];
export const DOWNLINE_CHANGE_REQUEST_STATUS_LABELS = {
    SUBMITTED: "Submitted",
    APPROVED: "Approved",
    REJECTED: "Rejected",
    CANCELLED: "Cancelled",
};
export function isDownlineChangeContractType(value) {
    return (typeof value === "string" &&
        DOWNLINE_CHANGE_CONTRACT_TYPES.includes(value));
}
export function isDownlineChangeCommissionType(value) {
    return (typeof value === "string" &&
        DOWNLINE_CHANGE_COMMISSION_TYPES.includes(value));
}
/** True when the row asks for a new upline or a new level. */
export function itemHasChange(item) {
    return Boolean(item.newUplineName?.trim() || item.newLevel?.trim());
}
/** True when anything on this carrier is actually being changed. */
export function carrierHasChange(carrier) {
    return (carrier.contractType !== "NO_CHANGE" ||
        carrier.commissionType !== "NO_CHANGE" ||
        carrier.items.some(itemHasChange));
}
/** A request with every carrier left on "No change" has nothing to submit. */
export function payloadHasChange(payload) {
    return payload.carriers.some(carrierHasChange);
}
export function carrierPlanLabel(carrierSlug) {
    const carrier = CARRIER_SLUG_TO_CARRIER[carrierSlug.trim().toLowerCase()];
    return carrier ? CARRIER_DISPLAY_NAMES[carrier] : carrierSlug.replace(/[_-]+/g, " ");
}
function optionalText(value, max = 200) {
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim();
    if (!trimmed)
        return null;
    return trimmed.slice(0, max);
}
function parseItem(value, carrierLabel, errors) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        errors.push(`${carrierLabel}: each product row must be an object`);
        return null;
    }
    const row = value;
    const contractId = optionalText(row.contractId, 64);
    const plan = optionalText(row.plan, 200);
    if (!contractId || !plan) {
        errors.push(`${carrierLabel}: a product row is missing its contract or plan`);
        return null;
    }
    return {
        contractId,
        plan,
        currentUplineName: optionalText(row.currentUplineName),
        currentUplineNumber: optionalText(row.currentUplineNumber, 64),
        currentLevel: optionalText(row.currentLevel, 64),
        newUplineName: optionalText(row.newUplineName),
        newUplinePortalUserId: optionalText(row.newUplinePortalUserId, 64),
        newLevel: optionalText(row.newLevel, 64),
    };
}
/**
 * Validate a submitted change request. Returns the normalized payload, or the
 * reasons it cannot be submitted — never a partially trusted object.
 */
export function parseDownlineChangeRequestPayload(value) {
    const errors = [];
    const body = value && typeof value === "object" && !Array.isArray(value) ? value : null;
    const rawCarriers = body?.carriers;
    if (!Array.isArray(rawCarriers) || rawCarriers.length === 0) {
        return { errors: ["Select at least one carrier and contract"] };
    }
    const carriers = [];
    const seen = new Set();
    for (const rawCarrier of rawCarriers) {
        if (!rawCarrier || typeof rawCarrier !== "object" || Array.isArray(rawCarrier)) {
            errors.push("Each carrier selection must be an object");
            continue;
        }
        const entry = rawCarrier;
        const carrierSlug = optionalText(entry.carrier, 64)?.toLowerCase() ?? null;
        if (!carrierSlug || !CARRIER_SLUG_TO_CARRIER[carrierSlug]) {
            errors.push(`Unknown carrier: ${carrierSlug ?? "(none)"}`);
            continue;
        }
        const label = carrierPlanLabel(carrierSlug);
        const contractId = optionalText(entry.contractId, 64);
        if (!contractId) {
            errors.push(`${label}: choose a contract`);
            continue;
        }
        const key = `${carrierSlug}:${contractId}`;
        if (seen.has(key)) {
            errors.push(`${label}: the same contract was selected twice`);
            continue;
        }
        seen.add(key);
        if (!isDownlineChangeContractType(entry.contractType)) {
            errors.push(`${label}: choose a contract type`);
            continue;
        }
        if (!isDownlineChangeCommissionType(entry.commissionType)) {
            errors.push(`${label}: choose a commission type`);
            continue;
        }
        const rawItems = Array.isArray(entry.items) ? entry.items : [];
        const items = [];
        for (const rawItem of rawItems) {
            const item = parseItem(rawItem, label, errors);
            if (item)
                items.push(item);
        }
        carriers.push({
            carrier: carrierSlug,
            contractId,
            contractNumber: optionalText(entry.contractNumber, 64),
            contractType: entry.contractType,
            commissionType: entry.commissionType,
            items,
        });
    }
    if (errors.length > 0)
        return { errors };
    if (carriers.length === 0)
        return { errors: ["Select at least one carrier and contract"] };
    const payload = { carriers };
    if (!payloadHasChange(payload)) {
        return {
            errors: ["Nothing to submit — every carrier is set to “No change”"],
        };
    }
    return { payload };
}
